<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { createLatestRequestGuard } from "@/helpers/latestRequest";
import { getCancelNotificationWarning } from "@/modules/companyNeed/helpers/cancelNotification";
import missionService from "@/modules/companyNeed/services/missionService";
import { type CancelMissionData, CancelNotificationWarning } from "@/modules/companyNeed/types/missions";

import type { SelectOption } from "@/dsComponents/inputs/types";
import type { LabelValue } from '@/types/common';

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Select from "@/dsComponents/inputs/Select.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
// Import components
import Modal from "@/dsComponents/modals/Modal.vue";

interface CancelMissionProps {
    open: boolean;
    missionId: string;
    /** Called once the command succeeded. */
    onSuccess?: () => void;
    /** Applies the cancellation optimistically and returns its rollback. */
    onApply?: () => () => void;
}

const CANCEL_REASONS = {
    clientNeedNoLongerExists: "CLIENT_NEED_NO_LONGER_EXISTS",
    fasterCompetition: "FASTER_COMPETITION",
    noAvailableWorker: "NO_AVAILABLE_WORKER",
    workerDidNotShowUp: "WORKER_DID_NOT_SHOW_UP",
    customerChanges: "CUSTOMER_CHANGES",
    myNeedNoLongerExists: "MY_NEED_NO_LONGER_EXISTS",
    needTreatedByOther: "NEED_TREATED_BY_OTHER",
    other: "OTHER",
} as const;

const props = defineProps<CancelMissionProps>();
const { t } = useI18n();

const emit = defineEmits<{
    (e: "close"): void;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    (name: "cancel", missionId: any): void;
}>();

const toaster = useToast();

const loading = ref(false);
const selectedReason = ref<LabelValue>();
const explanation = ref<string>("");
const notificationWarning = ref<CancelNotificationWarning | null>(null);
const isContentReady = ref(false);
const missionRequests = createLatestRequestGuard();
const cancelReasonsOptions = Object.keys(CANCEL_REASONS).map((key) => ({
    label: t(`page.companyNeed.missions.cancelReasons.${key}`),
    value: CANCEL_REASONS[key as keyof typeof CANCEL_REASONS],
}));

const selectedReasonValue = computed({
    get: () => selectedReason.value?.value || "",
    set: (value: string) => {
        selectedReason.value = undefined;
        if (value) {
            selectedReason.value = cancelReasonsOptions.find((reason: LabelValue) => value === reason.value);
        }
    },
});

const notificationMessage = computed(() => {
    switch (notificationWarning.value) {
        case CancelNotificationWarning.NOT_STARTED:
            return t("page.companyNeed.missions.cancelModal.notificationNotStarted");
        case CancelNotificationWarning.IN_PROGRESS:
            return t("page.companyNeed.missions.cancelModal.notificationInProgress");
        case CancelNotificationWarning.ENDED:
        case null:
            return "";
        default: {
            const exhaustiveCheck: never = notificationWarning.value;
            return exhaustiveCheck;
        }
    }
});

const resetForm = () => {
    explanation.value = "";
    selectedReason.value = undefined;
    notificationWarning.value = null;
    isContentReady.value = false;
};

const handleReasonSelect = (option: SelectOption) => {
    selectedReason.value = { label: option.label ?? "", value: option.value };
};

const handleClose = () => {
    missionRequests.invalidate();
    resetForm();
    emit("close");
};

watch(
    () => [props.open, props.missionId] as const,
    async ([isOpen, missionId]) => {
        const isLatest = missionRequests.start();
        if (!isOpen || !missionId) {
            notificationWarning.value = null;
            isContentReady.value = false;
            return;
        }

        isContentReady.value = false;
        notificationWarning.value = null;

        try {
            const mission = await missionService.fetchMissionDetails(missionId);
            if (!isLatest()) {
                return;
            }
            notificationWarning.value = getCancelNotificationWarning(mission);
        } catch {
            if (!isLatest()) {
                return;
            }
            notificationWarning.value = null;
        } finally {
            if (isLatest()) {
                isContentReady.value = true;
            }
        }
    },
    { immediate: true },
);

const cancelMissionConfirm = async () => {
    let rollback: (() => void) | undefined;
    try {
        loading.value = true;
        const data: CancelMissionData = {
            cancelReason: selectedReason.value?.value,
            cancelExplanation: explanation.value,
        };

        rollback = props.onApply?.();
        await missionService.cancelMission(props.missionId, data);
        rollback = undefined;

        toaster?.open({
            message: t("page.companyNeed.missions.cancelModal.success"),
            type: "success",
            duration: 5000,
        });

        props.onSuccess?.();
        emit("close");
    } catch (err) {
        rollback?.();
        console.error("Error cancelling mission:", err);

        toaster?.open({
            message: t("page.companyNeed.missions.cancelModal.error"),
            type: "error",
            duration: 5000,
        });
    } finally {
        loading.value = false;
    }
};
</script>
<template>
    <Modal :open="open && isContentReady" :title="t('page.companyNeed.missions.cancelModal.title')" width="500px" @close="handleClose">
        <template #default>
            <div class="cancel-mission-modal-content">
                <span class="modal-description typo-sm-regular">{{ t("page.companyNeed.missions.cancelModal.description") }}</span>
                <span v-if="notificationMessage" class="modal-description typo-sm-regular">{{ notificationMessage }}</span>
                <Select
                    :options="cancelReasonsOptions"
                    v-model="selectedReasonValue"
                    :label="t('form.reason')"
                    :placeholder="t('page.companyNeed.missions.cancelModal.reasonPlaceholder')"
                    icon="selector"
                    size="xs"
                    width="100%"
                    @select="handleReasonSelect"
                />
                <TextareaInput v-if="selectedReason?.value === CANCEL_REASONS.other" label="" v-model="explanation" :rows="3" />
            </div>
        </template>
        <template #footer>
            <div class="button-container">
                <ButtonAlt type="neutral" variant="ghost" :label="t('global.actions.cancel')" @click="handleClose()" :loading="loading" />
                <ButtonAlt
                    type="error"
                    variant="filled"
                    @click="cancelMissionConfirm()"
                    :label="t('page.companyNeed.missions.cancelModal.action')"
                    :loading="loading"
                />
            </div>
        </template>
    </Modal>
</template>
<style scoped lang="scss">
.button-container {
    gap: var(--spacing-lg, 12px);
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.cancel-mission-modal-content {
    gap: var(--spacing-xl, 16px);
    display: flex;
    flex-direction: column;
}
</style>
